/**
 * Presentation data for items: label, one-line flavour, tint and a vector glyph.
 *
 * This intentionally re-states a little of `survival/items.ts`. The UI only
 * ever learns about items through `inventory:changed`, which carries kinds and
 * counts, and subsystems are not allowed to import each other — so the HUD
 * keeps its own table of how a kind should read on screen.
 *
 * Glyphs are stroked paths in a 24x24 box, drawn in `currentColor` so a slot
 * can tint them by setting `color`.
 */

import type { ItemKind } from '../core/types';

export interface ItemUI {
  label: string;
  /** One dry line. Shown in the inventory detail pane. */
  description: string;
  /** CSS colour for the glyph. Muted on purpose: nothing here outshines amber. */
  tint: string;
  /** Inline SVG contents. */
  glyph: string;
  /**
   * Whether the inventory offers a use action. Mirrors `ItemDef.consumable`;
   * the survival system still has the final say and may refuse the use.
   */
  usable: boolean;
}

export const ITEM_UI: Record<ItemKind, ItemUI> = {
  'almond-water': {
    label: 'Almond Water',
    description: 'Tastes like almonds. Mostly.',
    tint: '#cfe0c8',
    glyph:
      '<path d="M6.6 9.6 12 4.8l5.4 4.8V20H6.6z"/><path d="M6.6 9.6h10.8"/><path d="M8.6 13.2h6.8"/><path d="M8.6 16.2h6.8"/>',
    usable: true,
  },
  'water-bottle': {
    label: 'Water Bottle',
    description: 'Warm, sealed, and perfectly fine.',
    tint: '#9fc4d8',
    glyph:
      '<path d="M10.2 3.5h3.6v2c0 1 2.1 1.7 2.1 3.6V19a2 2 0 0 1-2 2h-3.8a2 2 0 0 1-2-2V9.1c0-1.9 2.1-2.6 2.1-3.6z"/><path d="M8.1 11.4h7.8"/>',
    usable: true,
  },
  granola: {
    label: 'Granola Bar',
    description: 'Oats and something sweet.',
    tint: '#c4a06a',
    glyph: '<path d="M3.6 9.2h16.8v5.6H3.6z"/><path d="M7 9.2 5.4 14.8"/><path d="M11 9.2 9.4 14.8"/><path d="M15 9.2l-1.6 5.6"/><path d="M19 9.2l-1.6 5.6"/>',
    usable: true,
  },
  'canned-food': {
    label: 'Canned Food',
    description: 'The pull tab still works.',
    tint: '#b0b6ae',
    glyph:
      '<path d="M7 5.6c0-1 2.2-1.8 5-1.8s5 .8 5 1.8-2.2 1.8-5 1.8-5-.8-5-1.8z"/><path d="M7 5.6v12.8c0 1 2.2 1.8 5 1.8s5-.8 5-1.8V5.6"/><path d="M7 10.4h10"/><path d="M7 14.6h10"/>',
    usable: true,
  },
  candy: {
    label: 'Candy',
    description: "From someone's pocket. Not yours.",
    tint: '#d08fa8',
    glyph: '<path d="M9 9h6v6H9z"/><path d="M9 12 4.8 8.8v6.4z"/><path d="M15 12l4.2-3.2v6.4z"/>',
    usable: true,
  },
  battery: {
    label: 'Battery',
    description: 'Half a charge, if you are lucky.',
    tint: '#d2b24a',
    glyph: '<path d="M4 8h13v8H4z"/><path d="M17 10.6h2.6v2.8H17z"/><path d="m11.4 9.4-2.2 3.4h2.4l-1 2.2"/>',
    usable: true,
  },
  flashlight: {
    label: 'Flashlight',
    description: 'Yours now. Do not waste it.',
    tint: '#d8d2b8',
    glyph:
      '<path d="M3.6 10h7.2v4H3.6z"/><path d="M10.8 8.2h3.6v7.6h-3.6z"/><path d="M16.4 9.2 20 7.6"/><path d="M16.6 12h3.8"/><path d="M16.4 14.8 20 16.4"/>',
    usable: false,
  },
  bandage: {
    label: 'Bandage',
    description: 'Clean enough.',
    tint: '#ddd6c6',
    glyph:
      '<path d="M4.6 12.9 12.9 4.6a3.4 3.4 0 0 1 4.8 4.8l-8.3 8.3a3.4 3.4 0 1 1-4.8-4.8z"/><path d="M8.6 8.9 15.4 15.7" opacity=".5"/><path d="M9.4 12.2h.02"/><path d="M12.2 9.4h.02"/><path d="M12.2 15h.02"/><path d="M15 12.2h.02"/>',
    usable: true,
  },
  pills: {
    label: 'Pills',
    description: 'Unlabelled. Two left.',
    tint: '#b9a9d0',
    glyph: '<path d="M3.8 9.4h9.6a2.8 2.8 0 0 1 0 5.6H3.8a2.8 2.8 0 0 1 0-5.6z"/><path d="M8.6 9.4V15"/><path d="M18 10.6h.02"/><path d="M20.4 13.6h.02"/>',
    usable: true,
  },
  note: {
    label: 'Note',
    description: 'Handwriting that starts out steady.',
    tint: '#d9d2b4',
    glyph: '<path d="M6.8 3.6h7.4l4 4V20.4H6.8z"/><path d="M14.2 3.6v4h4"/><path d="M9.4 12h6"/><path d="M9.4 15.2h6"/><path d="M9.4 8.8h3"/>',
    usable: true,
  },
  key: {
    label: 'Key',
    description: 'Fits something. Somewhere.',
    tint: '#c9b26a',
    glyph: '<path d="M8 8.6a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8z"/><path d="M11.4 12H20.4"/><path d="M17.2 12v3"/><path d="M19.6 12v2.2"/>',
    usable: false,
  },
};

/** A snapshot of one inventory slot for display. Reused per frame; do not retain. */
export interface SlotView {
  kind: ItemKind | null;
  count: number;
}
